import { z } from "zod";

// Sequenciamento da fila de Ordens de Produção.
// O lote de sequência (`SimulationBatch.kind = "SEQUENCE"`) compara ORDENAÇÕES da fila real de
// uma linha (baseline / EDD / agrupada por produto) em vez de variar parâmetros de máquina.
// Reusa a orquestração, o ranking e as telas de progresso do lote automático.

// Objetivos EXCLUSIVOS de sequência — todos "menor é melhor". Enum próprio de propósito: o
// `SimulationObjectiveSchema` (lote PARAMS) NÃO os aceita e vice-versa (guard no service nas
// duas direções). No banco a coluna `objective` é única (enum Prisma estendido).
export const SequenceObjectiveSchema = z.enum(["TOTAL_LATENESS", "TOTAL_SETUP", "MAKESPAN"]);
export type SequenceObjective = z.infer<typeof SequenceObjectiveSchema>;

// POST /api/v1/lines/:lineId/sequence-batches (GESTOR)
// A fila NÃO vem no body: o service carrega TODAS as OPs SCHEDULED da linha (fila inteira) e
// tira um snapshot — é ele quem prova o staleness no apply.
// Erros: 400 SEQUENCE_QUEUE_TOO_SMALL (<2 SCHEDULED); 409 SCENARIO_PRODUCT_INCOMPATIBLE
// (gate de compatibilidade para TODOS os produtos da fila, pré-enqueue).
export const CreateSequenceBatchRequestSchema = z
  .object({
    objective: SequenceObjectiveSchema,
  })
  .strict();
export type CreateSequenceBatchRequest = z.infer<typeof CreateSequenceBatchRequestSchema>;

// Resultado por OP de um candidato (dentro de `Simulation.results.perOrder`). Tempos em
// segundos relativos à âncora do lote. `endS: null` ⇔ a ordem NÃO concluiu no horizonte
// (parcial EXPLÍCITO — nunca silencioso; bloqueia o apply com SEQUENCE_INCOMPLETE).
export const SequenceOrderResultSchema = z.object({
  orderId: z.string().uuid(),
  startS: z.number(),
  endS: z.number().nullable(),
  leadTimeS: z.number().nullable(),
  latenessS: z.number().nullable(), // null quando a OP não tem prazo
  setupS: z.number(),
  // Unidades que saíram da linha nesta ordem — quantifica o parcial em vez de deixar "não coube"
  // sem tamanho. `.default(0)` p/ resultados gravados antes do campo existir.
  units: z.number().int().nonnegative().default(0),
  completed: z.boolean(),
});
export type SequenceOrderResult = z.infer<typeof SequenceOrderResultSchema>;

// POST /api/v1/production-orders/apply-sequence (GESTOR)
// `simulationId` = candidato escolhido (qualquer um do lote, não só o vencedor).
// Erros: 409 SEQUENCE_STALE (alguma OP mudou desde a simulação — ZERO OPs alteradas);
//        409 SEQUENCE_INCOMPLETE (candidato com OP não concluída no horizonte).
export const ApplySequenceRequestSchema = z
  .object({
    simulationId: z.string().uuid(),
  })
  .strict();
export type ApplySequenceRequest = z.infer<typeof ApplySequenceRequestSchema>;

// Resultado de sequência de UM candidato, exposto na leitura do lote (`AutoCandidate.sequence`).
// Ausente/nulo em lotes PARAMS — é o discriminante prático na UI, junto do `kind` do lote.
export const SequenceCandidateResultSchema = z.object({
  perOrder: z.array(SequenceOrderResultSchema),
  totalSetupS: z.number(),
});
export type SequenceCandidateResult = z.infer<typeof SequenceCandidateResultSchema>;
